import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import { referenceImages, referenceFilterLabels } from "../data/content";

const ReferencesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState(searchParams.get("filter") || "osszes");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const getFilteredImages = () => {
    if (activeFilter === "osszes") {
      return Object.values(referenceImages).flat();
    }
    return referenceImages[activeFilter] || [];
  };

  const filteredImages = getFilteredImages();

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setSearchParams({ filter });
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  }, [filteredImages.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  }, [filteredImages.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") { e.preventDefault(); closeLightbox(); return; }
      if (e.key === "ArrowLeft") { e.preventDefault(); goToPrevious(); }
      if (e.key === "ArrowRight") { e.preventDefault(); goToNext(); }
    };
    if (lightboxOpen) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, goToPrevious, goToNext]);

  useEffect(() => {
    const filter = searchParams.get("filter");
    if (filter && referenceFilterLabels[filter]) {
      setActiveFilter(filter);
    }
  }, [searchParams]);

  return (
    <div data-testid="references-page">
      <SEO path="/referenciak" />
      {/* Hero */}
      <section className="relative py-8 sm:py-10 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="w-12 h-1 bg-amber-500 mb-3" />
          <h1 className="text-2xl sm:text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Referenciák
          </h1>
          <p className="text-stone-300 mt-2 max-w-2xl text-sm">
            Tekintse meg elkészült projektjeinket és inspirálódjon.
          </p>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-8 bg-white border-b border-stone-200 sticky top-24 sm:top-28 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            {Object.entries(referenceFilterLabels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleFilterChange(key)}
                className={`px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium transition-colors ${
                  activeFilter === key
                    ? "bg-amber-600 text-white"
                    : "bg-stone-100 text-slate-600 hover:bg-stone-200"
                }`}
                data-testid={`filter-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 sm:py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {filteredImages.map((image, index) => (
              <button
                key={`${image.id}-${activeFilter}`}
                onClick={() => openLightbox(index)}
                className="aspect-square overflow-hidden group cursor-pointer"
                data-testid={`gallery-image-${image.id}`}
              >
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Tetszik amit lát?
          </h2>
          <p className="text-slate-600 mb-8">
            Kérjen ingyenes helyszíni felmérést és személyre szabott árajánlatot!
          </p>
          <Button 
            asChild
            className="rounded-none px-10 py-6 bg-amber-600 text-white hover:bg-amber-700 font-medium text-sm tracking-wider uppercase"
          >
            <Link to="/arajanlat-keres">Ajánlatot kérek</Link>
          </Button>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
            data-testid="lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <div className="max-w-5xl max-h-[80vh] px-16" onClick={(e) => e.stopPropagation()}>
            <img 
              src={filteredImages[currentImageIndex]?.src}
              alt={filteredImages[currentImageIndex]?.alt}
              className="max-w-full max-h-[80vh] object-contain"
            />
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {currentImageIndex + 1} / {filteredImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferencesPage;
